import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getAllTaskComments, createTaskComment } from '../services/api/implementation/impl';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';

const TaskCommentPage = ({ taskID }) => {
  const userId = Cookies.get('userId');
  const [text, setText] = useState('');
  const userID = Cookies.get('userId');

  const {
    data: allComments = [],
    refetch: refetchComments,
    isFetching
  } = useQuery({
    queryKey: ['getAllTaskComments', taskID],
    queryFn: () => taskID && taskID !== '' && taskID !== null && getAllTaskComments(taskID),
    enabled: !!taskID
  });

  // Track page view
  useEffect(() => {
    mixpanel.track('Task Comment Page Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (taskID) {
      refetchComments();
    }
  }, [taskID]);

  const { mutate: addComment, isLoading: isSubmitting } = useMutation({
    mutationFn: (payload) => createTaskComment(payload),
    onSuccess: async () => {
      setText('');
      await refetchComments();
    },
    onError: (err) => {
      console.error('Error creating new comment', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating comment');
    }
  });

  const onAddClick = () => {
    if (!text.trim()) return;
    const payload = {
      task_id: taskID,
      comment: text
    };
    addComment(payload);
  };

  return (
    <div className="flex flex-col h-[92dvh] w-[100%] p-4">
      <div className="flex-1 overflow-y-auto space-y-4">
        {allComments.data &&
          allComments.data.map((comment, index) => {
            const isCurrentUser = comment.user_id === Number(userID);
            const userImage = comment.user?.user_pic?.url;
            const userNameInitial = comment.user?.name?.charAt(0).toUpperCase() || '?';
            const images = comment.images || [];
            // The comment row has no timestamp of its own; an attached image's
            // created_at is the closest thing to when it was posted.
            const postedAt = images[0]?.created_at || comment.user?.updated_at;
            const messageTime = postedAt
              ? new Date(postedAt).toLocaleString([], {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              : '';

            // function to check if image url is valid
            const isValidImageUrl = (url) => {
              return (
                url &&
                (url.endsWith('.jpg') ||
                  url.endsWith('.jpeg') ||
                  url.endsWith('.png') ||
                  url.endsWith('.webp'))
              );
            };

            return (
              <div
                key={index}
                className={`flex items-end ${isCurrentUser ? 'justify-end' : 'justify-start'} gap-2`}>
                {/* If not current user, show their DP first */}
                {!isCurrentUser &&
                  (isValidImageUrl(userImage) ? (
                    <img
                      src={userImage}
                      alt="User DP"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-sm font-bold">
                      {userNameInitial}
                    </div>
                  ))}

                {/* Message */}
                <div className="max-w-xs p-3 rounded-lg bg-gray-100 min-w-[10rem]">
                  {images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {images.map((image) => (
                        <a
                          key={image.id}
                          href={image.url}
                          target="_blank"
                          rel="noopener noreferrer">
                          <img
                            src={image.url}
                            alt={image.name || 'Comment attachment'}
                            loading="lazy"
                            className="w-32 h-32 rounded-lg object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                  {comment.comment && <p className="text-sm mb-1">{comment.comment}</p>}
                  <div className="text-xs text-gray-500 flex justify-between items-center">
                    <span>{comment.user?.name}</span>
                    <span>{messageTime}</span>
                  </div>
                </div>

                {/* If current user, show own DP after the message */}
                {isCurrentUser &&
                  (isValidImageUrl(userImage) ? (
                    <img
                      src={userImage}
                      alt="User DP"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-sm font-bold">
                      {userNameInitial}
                    </div>
                  ))}
              </div>
            );
          })}
      </div>

      <div className="flex items-center mt-4 p-2 bg-white rounded-lg shadow-md">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="flex-1 p-2 border-none focus:ring-0 outline-none text-sm"
          placeholder="Type anything here..."
        />
        <button
          onClick={onAddClick}
          disabled={isSubmitting}
          className="ml-2 p-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white pl-4 pr-4">
          ➤
        </button>
      </div>
    </div>
  );
};

export default TaskCommentPage;
